/**
 * Hook pour recuperer la liste des entreprises (admin)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminEntreprise {
  id: string;
  nom: string;
  siret: string | null;
  proprietaire_email: string | null;
  proprietaire_nom: string | null;
  plan: string | null;
  status: string | null;
  chantiers_count: number;
  membres_count: number;
  ocr_used: number;
  ocr_limit: number;
  ia_used: number;
  ia_limit: number;
  created_at: string;
}

interface UseAdminEntreprisesOptions {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useAdminEntreprises(options: UseAdminEntreprisesOptions = {}) {
  const { page = 1, pageSize = 20, search = '' } = options;

  const [entreprises, setEntreprises] = useState<AdminEntreprise[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchEntreprises();
  }, [page, pageSize, search]);

  const fetchEntreprises = async () => {
    try {
      setLoading(true);
      setError(null);

      // Construire la requete de base
      let query = supabase
        .from('entreprises')
        .select(`
          id,
          nom,
          siret,
          proprietaire_user_id,
          created_at
        `, { count: 'exact' });

      // Filtres de recherche
      if (search) {
        query = query.or(`nom.ilike.%${search}%,siret.ilike.%${search}%`);
      }

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to).order('created_at', { ascending: false });

      const { data: entreprisesData, count, error: queryError } = await query;

      if (queryError) throw queryError;

      // Pour chaque entreprise, recuperer les donnees complementaires
      const transformedEntreprises: AdminEntreprise[] = await Promise.all(
        (entreprisesData || []).map(async (entreprise) => {
          // Recuperer le proprietaire
          const { data: profileData } = await supabase
            .from('profiles')
            .select('email, prenom, nom')
            .eq('id', entreprise.proprietaire_user_id)
            .single();

          // Recuperer la subscription
          const { data: subData } = await supabase
            .from('subscriptions')
            .select('plan, status, ocr_used, max_ocr, analyses_ia_used, max_analyses_ia')
            .eq('entreprise_id', entreprise.id)
            .single();

          // Compter les chantiers
          const { count: chantiersCount } = await supabase
            .from('chantiers')
            .select('*', { count: 'exact', head: true })
            .eq('entreprise_id', entreprise.id);

          // Compter les membres d'equipe
          const { count: membresCount } = await supabase
            .from('membres_equipe')
            .select('*', { count: 'exact', head: true })
            .eq('entreprise_id', entreprise.id);

          return {
            id: entreprise.id,
            nom: entreprise.nom,
            siret: entreprise.siret,
            proprietaire_email: profileData?.email || null,
            proprietaire_nom: profileData
              ? `${profileData.prenom || ''} ${profileData.nom || ''}`.trim()
              : null,
            plan: subData?.plan || 'free',
            status: subData?.status || 'active',
            chantiers_count: chantiersCount || 0,
            membres_count: membresCount || 0,
            ocr_used: subData?.ocr_used || 0,
            ocr_limit: subData?.max_ocr || 20,
            ia_used: subData?.analyses_ia_used || 0,
            ia_limit: subData?.max_analyses_ia || 10,
            created_at: entreprise.created_at,
          };
        })
      );

      setEntreprises(transformedEntreprises);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('[useAdminEntreprises] Erreur:', err);
      setError(err instanceof Error ? err : new Error('Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return {
    entreprises,
    totalCount,
    totalPages: Math.ceil(totalCount / pageSize),
    loading,
    error,
    refetch: fetchEntreprises,
  };
}
